import moment from 'moment';

/**
 * Upcase the first letter of a string
 */
export const upcaseFirst = (str: string) => (str ? str.charAt(0).toUpperCase() + str.slice(1) : '');
/**
 * Formats a month bitmask for manual bonus period display
 * @param bitmask number
 * @param periodType string
 */
export const formatMonthBitmask = (bitmask: number, periodType: string) => {
  if (periodType === 'month') {
    // Find the first set bit and return its month name
    for (let i = 0; i < 12; i++) {
      if (bitmask & (1 << i)) {
        return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][i];
      }
    }
    return '';
  }
  if (periodType === 'quarter') {
    // Q1: Jan-Mar (bits 0-2), Q2: Apr-Jun (3-5), Q3: Jul-Sep (6-8), Q4: Oct-Dec (9-11)
    if (bitmask <= quarterOptions[0].value) return 'Q1';
    if (bitmask === quarterOptions[1].value) return 'Q2';
    if (bitmask === quarterOptions[2].value) return 'Q3';
    if (bitmask === quarterOptions[3].value) return 'Q4';
    return '';
  }
  if (periodType === 'halfyear') {
    // H1: Jan-Jun (bits 0-5), H2: Jul-Dec (6-11)
    if (bitmask <= halfyearOptions[0].value) return 'H1';
    if (bitmask <= halfyearOptions[1].value) return 'H2';
    return '';
  }
  if (periodType === 'year') {
    // All months set
    if (bitmask === 0b111111111111) return '';
    return '';
  }
  return '';
};

const extractQuarterFromSalesComp = (salesComp) => {
  if (salesComp) {
    if (salesComp.quarter) {
      return salesComp.quarter;
    }
    const startDate = moment(salesComp.startDate);
    if (startDate.isValid()) {
      const quaterStr = startDate.format('YYYY [Q]Q');
      return quaterStr;
    }
  }
  return '';
};

// Utility to extract and normalize bonuses from backend response
export function extractBonuses(data: any) {
  const manualBonuses = (data?.manualBonuses || []).map((bu: any) => ({
    id: bu.manualBonusUserId,
    type: 'Manual',
    periodType: bu.manualBonus?.periodType || '-',
    year: bu.manualBonus?.year || '-',
    monthBitmask: bu.manualBonus?.monthBitmask,
    manualBonusId: bu.manualBonus?.manualBonusId,
    amount: bu.bonusAmount,
    submissionDate: bu.manualBonus?.submissionDate || '',
    description: bu.manualBonus?.description || '',
    detailUrl: bu.manualBonusId ? `/manual-bonus/detail/${bu.manualBonusId}` : '',
    userId: bu.userId,
    user: bu.user || null,
    isManagedUser: bu.isManagedUser || false,
  }));
  const hdBonuses = (data?.hdBonuses || []).map((bonus: any) => ({
    id: bonus.submissionId,
    type: 'Health & Dev',
    periodType: 'quarter',
    year: bonus.year || '-',
    quarter: bonus.quarter || '-',
    month: bonus.month || '-',
    amount: bonus.totalBonusAmount,
    submissionDate: bonus.submissionDate || '',
    description: bonus.description || '',
    detailUrl: bonus.submissionId ? `/hd-bonus/${bonus.submissionId}` : '/hd-bonus/submission',
    userId: bonus.userId,
    user: bonus.user || null,
    isManagedUser: bonus.isManagedUser || false,
  }));
  const salesBonuses = (data?.salesBonuses || []).map((sb: any) => ({
    id: sb.salesCompRollUpId,
    type: 'Sales',
    periodType: 'quarter',
    year: extractQuarterFromSalesComp(sb.salesComp)?.split(' ')[0] || '-',
    quarter: extractQuarterFromSalesComp(sb.salesComp)?.split(' ')[1] || '-',
    startDate: sb.salesComp?.startDate || '',
    endDate: sb.salesComp?.endDate || '',
    amount: sb.compenstation,
    percentEarned: sb.percentEarned,
    totalContribution: sb.totalContribution,
    totalPotential: sb.totalPotential,
    submissionDate: sb.salesComp?.startDate || '',
    description: sb.fromEmployee
      ? `Contributed from ${[sb.user.firstName, sb.user.lastName].filter(Boolean).join(' ')}`
      : sb.isManagedUser
        ? `Sales Bonus for: ${[sb.user.firstName, sb.user.lastName].filter(Boolean).join(' ')}`
        : '',
    detailUrl: sb.salesComp ? `/sales-bonus/detail/${sb.salesComp.salesCompId}` : '',
    userId: sb.userId,
    user: sb.user || null,
    isManagedUser: sb.isManagedUser || false,
  }));

  const allBonuses = [...manualBonuses, ...hdBonuses, ...salesBonuses];
  return allBonuses
    .sort((a, b) => {
      const dateA = new Date(a.submissionDate || a.startDate || 0).getTime();
      const dateB = new Date(b.submissionDate || b.startDate || 0).getTime();
      return dateB - dateA;
    })
    .sort((a, b) => {
      // Move isManagedUser=true bonuses to the beginning
      if (a.isManagedUser === b.isManagedUser) return 0;
      return a.isManagedUser ? 1 : -1;
    });
}

export const monthOptions = [
  { value: 1 << 0, label: 'January' },
  { value: 1 << 1, label: 'February' },
  { value: 1 << 2, label: 'March' },
  { value: 1 << 3, label: 'April' },
  { value: 1 << 4, label: 'May' },
  { value: 1 << 5, label: 'June' },
  { value: 1 << 6, label: 'July' },
  { value: 1 << 7, label: 'August' },
  { value: 1 << 8, label: 'September' },
  { value: 1 << 9, label: 'October' },
  { value: 1 << 10, label: 'November' },
  { value: 1 << 11, label: 'December' },
];

export const quarterOptions = [
  { value: (1 << 0) | (1 << 1) | (1 << 2), label: 'Q1' }, // Jan-Mar
  { value: (1 << 3) | (1 << 4) | (1 << 5), label: 'Q2' }, // Apr-Jun
  { value: (1 << 6) | (1 << 7) | (1 << 8), label: 'Q3' }, // Jul-Sep
  { value: (1 << 9) | (1 << 10) | (1 << 11), label: 'Q4' }, // Oct-Dec
];

export const halfyearOptions = [
  { value: (1 << 0) | (1 << 1) | (1 << 2) | (1 << 3) | (1 << 4) | (1 << 5), label: 'H1' }, // Jan-Jun
  { value: (1 << 6) | (1 << 7) | (1 << 8) | (1 << 9) | (1 << 10) | (1 << 11), label: 'H2' }, // Jul-Dec
];
